import { Complete } from "@/components/common/Complete";
import {
	diaryByIdQueryOptions,
	useDiaryByIdQuery,
} from "@/features/diaries/api";
import useCountDueObservations from "@/features/observations/hooks/useCountDueObservations";
import useCountDueTrackers from "@/features/trackers/hooks/useCountDueTrackers";
import type { StatefulDiaryT } from "@/types";
import {
	randomHappyEmoji,
	wordsOfEncouragement,
	wordsOfPraise,
} from "@/utils/positivePatientWords";
import { createFileRoute } from "@tanstack/react-router";
import { CheckCircle2 } from "lucide-react";

const EntryComplete = () => {
	const { diaryId } = Route.useParams();

	const { data: diary, isFetching } = useDiaryByIdQuery(diaryId as string);

	const remainingObservationEntries = useCountDueObservations(
		diaryId as string,
	);
	const remainingTrackerEntries = useCountDueTrackers(diary as StatefulDiaryT);
	const remainingEntries =
		(remainingObservationEntries ?? 0) + (remainingTrackerEntries ?? 0);
	const isDue = Number(remainingEntries + remainingTrackerEntries) > 0;

	return (
		<Complete
			title="Great job!"
			description={
				isDue
					? `You have ${remainingEntries} more ${
							remainingEntries === 1 ? "entry" : "entries"
						} to complete. ${wordsOfEncouragement()} ${randomHappyEmoji()}`
					: `You've completed all your entries for now. ${wordsOfPraise()}`
			}
			icon={CheckCircle2}
			isLoading={isFetching}
			loadingText="Almost there!"
			loadingDescription={wordsOfPraise()}
			primaryButton={{
				text: isDue ? "Continue to Next Entry" : "See your Charts",
				to: isDue
					? "/diaries/$diaryId/home/new"
					: "/diaries/$diaryId/home/charts",
				params: { diaryId },
			}}
		/>
	);
};

export const Route = createFileRoute(
	"/_app/diaries/$diaryId/observation/$observationId/entry/$entryId/complete/patient",
)({
	component: EntryComplete,
	loader: async ({ params, context }) => {
		const diary = await context.queryClient.ensureQueryData(
			diaryByIdQueryOptions(params.diaryId),
		);
		return {
			diary,
		};
	},
});
